// What's new (/whats-new, docs/factory.md "Screenshots and What's new"): every
// merged PR that says what it changed for the owner or shows it (before and
// after), newest merge first. An agent's PR says it in publish.json
// (`whatsnew`, `shots` on the AgentRun mirror); a PR opened from a laptop says
// it in its body, a ```whatsnew fence of JSON read off the PullRequest mirror
// ("PRs from a laptop"). A PR an agent run already covers is the run's. A
// feature is Live once prod's latest release is newer than its merge, else it
// is on its way in the next ship. Pure; the page draws it.
import type { AgentRunResource } from '@/kinds/agentrun/apply'
import type { RunShot } from '@/kinds/agentrun/schema'
import type { PullRequestResource } from '@/kinds/git/apply'
import type { Release } from '@/db/api'
import { BLOB_ID } from '@/lib/blobs'

/** The most features the page shows (Show less: no count of the rest). */
export const WHATS_NEW_MAX = 24

/** The limits publish.json has for an agent; a PR body is held to the same. */
export const TITLE_MAX = 80
export const EXPLAINER_MAX = 600
export const CAPTION_MAX = 140
export const SHOTS_MAX = 6
const NAME_MAX = 40

/** The factory's event for a merge (factoryprov.EventPRMerged); the short name is accepted too. */
const MERGE_EVENTS = new Set(['pull_request_merged', 'pr_merged'])

export interface Feature {
  id: string
  title: string
  explainer: string
  shots: RunShot[]
  /** when the PR merged (ISO) */
  merged: string
  pr?: number
}

/** When a run's PR merged: its merge event, else when the mirror last changed. */
export const mergedAt = (run: AgentRunResource): string => {
  const ev = (run.spec.events ?? []).filter((e) => e.kind && MERGE_EVENTS.has(e.kind) && e.ts).at(-1)
  return ev?.ts || run.spec.updated_at || run.updated_at
}

// ---- a PR body's ```whatsnew block ---------------------------------------------

/** What a PR body declares: the same shape as an agent's whatsnew and shots. */
export interface BodyWhatsNew {
  title: string
  explainer: string
  shots: RunShot[]
}

// the first fence whose info string is `whatsnew` (three or more backticks or
// tildes, closed by the same run), anywhere in the body
const FENCE = /^ {0,3}(`{3,}|~{3,})[ \t]*whatsnew[ \t]*\r?\n([\s\S]*?)\r?\n {0,3}\1[ \t]*$/m

const text = (v: unknown, max: number): string => {
  if (typeof v !== 'string') return ''
  const s = v.trim()
  return s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s
}

const blobId = (v: unknown): string | undefined => {
  if (typeof v !== 'string') return undefined
  const id = v.trim().toLowerCase()
  return BLOB_ID.test(id) ? id : undefined
}

const shotOf = (v: unknown, i: number): RunShot | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null
  const o = v as Record<string, unknown>
  const after = blobId(o.after)
  if (!after) return null
  const name =
    text(o.name, NAME_MAX)
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/^-+|-+$/g, '') || `shot-${i + 1}`
  const caption = text(o.caption, CAPTION_MAX)
  const before = blobId(o.before)
  return {
    name,
    viewport: o.viewport === 'desktop' ? 'desktop' : 'phone',
    after,
    ...(before ? { before } : {}),
    ...(caption ? { caption } : {}),
  }
}

/**
 * The ```whatsnew block of a PR body, tidied: null when there is none, it is
 * not a JSON object, or it says nothing (no title and no shot with an after).
 * Long words are clipped, a shot without a valid after is dropped and a bad
 * before becomes a new screen, names are made unique, at most six shots.
 */
export const whatsNewOfBody = (body: string | undefined | null): BodyWhatsNew | null => {
  const m = body ? FENCE.exec(body) : null
  if (!m) return null
  let raw: unknown
  try {
    raw = JSON.parse(m[2]!)
  } catch {
    return null
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const o = raw as Record<string, unknown>
  const seen = new Set<string>()
  const shots: RunShot[] = []
  for (const [i, v] of (Array.isArray(o.shots) ? o.shots : []).entries()) {
    if (shots.length >= SHOTS_MAX) break
    const s = shotOf(v, i)
    if (!s) continue
    // the page keys a feature's shots by name
    let name = s.name
    for (let n = 2; seen.has(name); n++) name = `${s.name}-${n}`
    seen.add(name)
    shots.push({ ...s, name })
  }
  const title = text(o.title, TITLE_MAX)
  if (!title && !shots.length) return null
  return { title, explainer: text(o.explainer, EXPLAINER_MAX), shots }
}

// ---- the features ------------------------------------------------------------------

const repoOf = (url: string) =>
  url
    .replace(/^https?:\/\/[^/]+\//, '')
    .replace(/\.git$/, '')
    .replace(/\/+$/, '')
    .toLowerCase()

const prKey = (repo: string, n: number) => `${repo.toLowerCase()}#${n}`

/** When a PR merged: the provider's merge time, else when the provider last changed it, else the mirror. */
export const pullMergedAt = (p: PullRequestResource): string =>
  p.spec.merged_at || p.spec.source.updated_at || p.updated_at

// by the instant, not the string: Forgejo's times carry the server's offset, the factory's are UTC
const instant = (iso: string) => {
  const t = Date.parse(iso)
  return Number.isNaN(t) ? 0 : t
}
const newestFirst = (a: Feature, b: Feature) => instant(b.merged) - instant(a.merged)

/**
 * The features: merged agent PRs with a what's new or shots, and merged PRs
 * whose body carries a ```whatsnew block that no agent run covers, newest
 * merge first, at most max.
 */
export const featuresOf = (
  runs: AgentRunResource[] | undefined,
  pulls: PullRequestResource[] | undefined = [],
  max = WHATS_NEW_MAX,
): Feature[] => {
  const live = (runs ?? []).filter((r) => !r.deleted_at)
  const fromRuns: Feature[] = live
    .filter(
      (r) =>
        r.spec.pr_state === 'merged' && (!!r.spec.whatsnew?.title || !!r.spec.shots?.some((s) => !!s.after)),
    )
    .map((r) => ({
      id: r.id,
      title: r.spec.whatsnew?.title || r.spec.report?.headline || r.spec.title || 'A change',
      explainer: r.spec.whatsnew?.explainer ?? '',
      shots: (r.spec.shots ?? []).filter((s) => !!s.after),
      merged: mergedAt(r),
      pr: r.spec.pr_number,
    }))
  // a PR an agent opened is the agent's, whatever its body says
  const covered = new Set(
    live
      .filter((r) => !!r.spec.pr_number)
      .map((r) =>
        prKey(repoOf(r.spec.repo_url || r.spec.pr_url.replace(/\/pulls?\/\d+.*$/, '')), r.spec.pr_number!),
      ),
  )
  const fromPulls: Feature[] = []
  for (const p of pulls ?? []) {
    if (p.deleted_at || p.spec.state !== 'merged' || covered.has(prKey(p.spec.repo, p.spec.number))) continue
    const w = whatsNewOfBody(p.spec.body)
    if (!w) continue
    fromPulls.push({
      id: p.id,
      title: w.title || p.spec.title || 'A change',
      explainer: w.explainer,
      shots: w.shots,
      merged: pullMergedAt(p),
      pr: p.spec.number,
    })
  }
  return [...fromRuns, ...fromPulls].sort(newestFirst).slice(0, max)
}

/** When prod last took a release: the live one's time, else the newest (absent: no answer). */
export const lastRelease = (releases: Release[] | undefined): string | null => {
  if (!releases?.length) return null
  const live = releases.find((r) => r.live)
  if (live) return live.at
  return (
    releases
      .map((r) => r.at)
      .sort()
      .at(-1) ?? null
  )
}

/** Live once prod's latest release came after the merge; null when that is not known. */
export const liveness = (merged: string, released: string | null): 'live' | 'next' | null => {
  if (!released || !merged) return null
  return Date.parse(released) >= Date.parse(merged) ? 'live' : 'next'
}
